// src/pages/MissionsPage.js – Cinematic enhanced
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { missionsAPI } from '../utils/api';
import { useSound } from '../hooks/useSound';
import { useCinematic } from '../hooks/useCinematic';
import useViewport from '../hooks/useViewport';

const LOCAL = {
  html: [
    { _id:'h1', title:'Your First HTML Page',   description:'Create the HTML boilerplate',         order:1, xpReward:50,  coinsReward:20, difficulty:'beginner' },
    { _id:'h2', title:'Titles & Headings',       description:'Add <title> and <h1>',               order:2, xpReward:50,  coinsReward:20, difficulty:'beginner' },
    { _id:'h3', title:'Paragraphs',              description:'Structure with p and h2',             order:3, xpReward:60,  coinsReward:25, difficulty:'beginner' },
    { _id:'h4', title:'Hyperlinks',              description:'Create clickable links',              order:4, xpReward:70,  coinsReward:30, difficulty:'beginner' },
    { _id:'h5', title:'Images',                  description:'Display images with <img>',           order:5, xpReward:70,  coinsReward:30, difficulty:'beginner' },
    { _id:'h6', title:'Lists',                   description:'Ordered and unordered lists',         order:6, xpReward:80,  coinsReward:35, difficulty:'beginner' },
    { _id:'h7', title:'Tables',                  description:'Data rows and columns',               order:7, xpReward:100, coinsReward:40, difficulty:'intermediate' },
    { _id:'h8', title:'Forms',                   description:'Input forms and buttons',             order:8, xpReward:110, coinsReward:45, difficulty:'intermediate', badge:true },
  ],
  css: [
    { _id:'c1', title:'First Styles',            description:'Colors and backgrounds',              order:1, xpReward:60,  coinsReward:25, difficulty:'beginner' },
    { _id:'c2', title:'Box Model',               description:'Margin, padding, borders',            order:2, xpReward:70,  coinsReward:30, difficulty:'beginner' },
    { _id:'c3', title:'Flexbox',                 description:'Flexible layouts',                    order:3, xpReward:100, coinsReward:40, difficulty:'intermediate' },
    { _id:'c4', title:'CSS Grid',                description:'2D grid layouts',                     order:4, xpReward:110, coinsReward:45, difficulty:'intermediate' },
    { _id:'c5', title:'Animations',              description:'Transitions and transforms',          order:5, xpReward:120, coinsReward:50, difficulty:'intermediate', badge:true },
    { _id:'c6', title:'Media Queries',           description:'Responsive design',                   order:6, xpReward:130, coinsReward:55, difficulty:'intermediate' },
  ],
  javascript: [
    { _id:'j1', title:'Variables',               description:'const, let, var',                     order:1, xpReward:70,  coinsReward:30, difficulty:'beginner' },
    { _id:'j2', title:'Functions',               description:'Write and call functions',             order:2, xpReward:80,  coinsReward:35, difficulty:'beginner' },
    { _id:'j3', title:'Arrays & Loops',          description:'Iterate over data',                   order:3, xpReward:90,  coinsReward:40, difficulty:'beginner' },
    { _id:'j4', title:'Objects',                 description:'Key-value pairs',                     order:4, xpReward:90,  coinsReward:40, difficulty:'beginner' },
    { _id:'j5', title:'DOM Manipulation',        description:'Change the page',                     order:5, xpReward:110, coinsReward:45, difficulty:'intermediate', badge:true },
    { _id:'j6', title:'Event Listeners',         description:'Click and input events',              order:6, xpReward:120, coinsReward:50, difficulty:'intermediate' },
    { _id:'j7', title:'Arrow Functions',         description:'Concise => syntax',                   order:7, xpReward:130, coinsReward:55, difficulty:'intermediate' },
    { _id:'j8', title:'Promises & Async',        description:'Async operations',                    order:8, xpReward:150, coinsReward:60, difficulty:'advanced' },
  ],
};

const TRACKS = [
  { id:'html',       label:'HTML',       icon:'🌐', color:'#ff6432' },
  { id:'css',        label:'CSS',        icon:'🎨', color:'#3296ff' },
  { id:'javascript', label:'JavaScript', icon:'⚡', color:'#ffd700' },
];

const DIFF_COLOR = { beginner:'#39ff14', intermediate:'#ffd700', advanced:'#ff6432' };

function hexRgb(hex) {
  const r=parseInt(hex.slice(1,3),16), g=parseInt(hex.slice(3,5),16), b=parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}

export default function MissionsPage() {
  const [params, setParams]    = useSearchParams();
  const [track,  setTrack]     = useState(params.get('track') || 'html');
  const [missions,setMissions] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { playClick, playHover, playTransition } = useSound();
  const { spawnButtonSparks } = useCinematic();
  const { isMobile } = useViewport();

  useEffect(() => {
    missionsAPI.getAll(track)
      .then(r => setMissions(r.data.missions))
      .catch(() => setMissions(LOCAL[track] || []));
  }, [track]);

  const changeTrack = (e, t) => {
    playTransition(); spawnButtonSparks(e, TRACKS.find(x=>x.id===t)?.color || '#00f5ff');
    setTrack(t); setParams({ track:t });
  };

  const isCompleted = id => (user?.completedMissions || []).some(m => m?.toString() === id || m === id);
  const isLocked    = i  => i > 0 && !isCompleted(missions[i-1]?._id);

  const info = TRACKS.find(t => t.id === track);
  const done = missions.filter(m => isCompleted(m._id)).length;

  return (
    <div className="page-shell">
      <div className="section-title animate-fade-in">MISSION CONTROL</div>

      {/* Track tabs */}
      <div className="animate-fade-in" style={{ display:'flex', gap:'10px', marginBottom:'22px', flexWrap:'wrap' }}>
        {TRACKS.map(t => {
          const d = (LOCAL[t.id] || []).filter(m => isCompleted(m._id)).length;
          const tot = LOCAL[t.id]?.length || 0;
          return (
            <button key={t.id} onClick={e => changeTrack(e, t.id)} style={{
              flex:'1 1 220px', minWidth:isMobile ? '100%' : '220px', padding:'13px 14px', borderRadius:'10px', cursor:'pointer', textAlign:'left',
              border:`1px solid ${track===t.id ? t.color : 'rgba(255,255,255,0.07)'}`,
              background: track===t.id ? `rgba(${hexRgb(t.color)},.11)` : 'rgba(255,255,255,0.02)',
              boxShadow: track===t.id ? `0 0 22px rgba(${hexRgb(t.color)},.16)` : 'none',
              transition:'all 0.22s cubic-bezier(0.16,1,0.3,1)',
            }}
              onMouseEnter={() => playHover()}
            >
              <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' }}>
                <span style={{ fontSize:'18px' }}>{t.icon}</span>
                <span style={{ fontFamily:'var(--font-game)', fontSize:'15px', fontWeight:700,
                               color: track===t.id ? t.color : 'rgba(255,255,255,0.55)', letterSpacing:'1px' }}>{t.label}</span>
                <span style={{ marginLeft:'auto', fontFamily:'var(--font-game)', fontSize:'15px',
                               color: track===t.id ? t.color : 'rgba(255,255,255,0.25)' }}>{d}/{tot}</span>
              </div>
              <div style={{ height:'3px', background:'rgba(255,255,255,0.05)', borderRadius:'2px' }}>
                <div style={{ height:'100%', width:`${tot?d/tot*100:0}%`, background:t.color, borderRadius:'2px', transition:'width 0.5s' }} />
              </div>
            </button>
          );
        })}
      </div>

      {/* Progress bar */}
      <div className="animate-fade-in" style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'18px' }}>
        <span style={{ fontFamily:'var(--font-game)', fontSize:'15px', color:info?.color, letterSpacing:'1px', flexShrink:0 }}>
          {info?.icon} {info?.label}
        </span>
        <div style={{ flex:1, height:'5px', background:'rgba(255,255,255,0.05)', borderRadius:'3px' }}>
          <div style={{ height:'100%', width:`${missions.length?done/missions.length*100:0}%`, background:info?.color, borderRadius:'3px', transition:'width 0.6s' }} />
        </div>
        <span style={{ fontFamily:'var(--font-game)', fontSize:'15px', fontWeight:700, color:info?.color, flexShrink:0 }}>{done}/{missions.length}</span>
      </div>

      {/* Mission grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:'13px' }}>
        {missions.map((m, i) => {
          const isDone = isCompleted(m._id);
          const locked = isLocked(i);
          return (
            <div key={m._id}
              onClick={e => { if(!locked){ playClick(); spawnButtonSparks(e, isDone?'#39ff14':'#00f5ff'); navigate(`/missions/${m._id}`); }}}
              style={{
                background: isDone ? 'rgba(57,255,20,0.04)' : 'rgba(13,17,23,0.9)',
                border:`1px solid ${isDone?'rgba(57,255,20,0.26)':locked?'rgba(255,255,255,0.04)':'rgba(0,245,255,0.1)'}`,
                borderRadius:'10px', padding:'17px', cursor: locked?'not-allowed':'pointer',
                opacity: locked ? 0.45 : 1, transition:'all 0.25s cubic-bezier(0.16,1,0.3,1)', position:'relative', overflow:'hidden',
                backdropFilter:'blur(6px)', WebkitBackdropFilter:'blur(6px)',
              }}
              onMouseEnter={e => {
                playHover();
                if(!locked){ e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.borderColor=isDone?'rgba(57,255,20,0.45)':'rgba(0,245,255,0.32)'; e.currentTarget.style.boxShadow=isDone?'0 8px 24px rgba(57,255,20,0.1)':'0 8px 24px rgba(0,245,255,0.08)'; }
              }}
              onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.borderColor=isDone?'rgba(57,255,20,0.26)':locked?'rgba(255,255,255,0.04)':'rgba(0,245,255,0.1)'; e.currentTarget.style.boxShadow=''; }}
            >
              {/* Top accent for completed */}
              {isDone && <div style={{ position:'absolute', top:0, left:0, right:0, height:'2px', background:'linear-gradient(90deg,transparent,#39ff14,transparent)', opacity:0.7 }} />}

              <div style={{ position:'absolute', top:'12px', right:'12px', width:'20px', height:'20px', borderRadius:'50%',
                            display:'flex', alignItems:'center', justifyContent:'center', fontSize:'15px',
                            background: isDone?'rgba(57,255,20,0.15)':locked?'rgba(255,255,255,0.04)':'rgba(0,245,255,0.07)',
                            border:`1px solid ${isDone?'rgba(57,255,20,0.4)':locked?'rgba(255,255,255,0.08)':'rgba(0,245,255,0.2)'}`,
                            color: isDone?'#39ff14':locked?'rgba(255,255,255,0.2)':'rgba(0,245,255,0.6)' }}>
                {isDone?'✓':locked?'🔒':'○'}
              </div>
              <div style={{ fontFamily:'var(--font-game)', fontSize:'15px', color:'rgba(255,255,255,0.22)', marginBottom:'4px', letterSpacing:'2px' }}>
                {track.toUpperCase()} #{m.order}
              </div>
              <div style={{ fontWeight:700, fontSize:'15px', color:'#fff', marginBottom:'5px' }}>{m.title}</div>
              <div style={{ fontSize:'15px', color:'rgba(255,255,255,0.38)', marginBottom:'12px', lineHeight:1.4 }}>{m.description}</div>
              <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                <span style={{ fontSize:'15px', fontWeight:700, color:'#7c3aed' }}>⚡{m.xpReward}</span>
                <span style={{ fontSize:'15px', fontWeight:700, color:'#ffd700' }}>🪙{m.coinsReward}</span>
                {(m.badge || m.badgeReward?.id) && <span style={{ fontSize:'15px', color:'#ffd700' }}>🏅</span>}
                <span style={{ marginLeft:'auto', fontSize:'15px', fontWeight:700, color:DIFF_COLOR[m.difficulty],
                               background:`rgba(${DIFF_COLOR[m.difficulty]==='#39ff14'?'57,255,20':m.difficulty==='intermediate'?'255,215,0':'255,100,50'},.08)`,
                               padding:'2px 7px', borderRadius:'4px', letterSpacing:'1px' }}>
                  {(m.difficulty||'').toUpperCase()}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
